import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Plus, ChevronDown, Check, Loader2, FolderPlus, CheckCircle2, ArrowLeft, Crown } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import api from '../../services/api';
import type { Category, Subcategory } from '../../types';

function toPascalCase(str: string): string {
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

function DynamicIcon({ name, className }: { name: string; className?: string }) {
  const pascalName = toPascalCase(name);
  const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[pascalName];
  if (!IconComponent) return null;
  return <IconComponent className={className} />;
}

interface TreeCategoryAutocompleteProps {
  categories: Category[];
  categoryId: string;
  subcategoryId: string;
  onChange: (categoryId: string, subcategoryId: string) => void;
  onCategoriesChange?: (categories: Category[]) => void;
  onConfirm?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  allowCreate?: boolean;
  categoryType?: 'income' | 'expense';
  forCouple?: boolean;
  className?: string;
  dataTutorial?: string;
  internalTransferCategoryId?: string;
  internalTransferSubcategories?: Array<{ id: string; name: string; icon: string; isDefault: boolean }>;
  disabledSubcategoryIds?: Record<string, string>;
}

// Modes du dropdown
type DropdownMode =
  | { kind: 'browse' }
  // "Ajouter sous-categorie" clique → choisir la categorie parente
  | { kind: 'pickParent'; subcategoryName: string }
  // "Ajouter categorie" clique → categorie creee, nommer la sous-categorie
  | { kind: 'nameSubcategory'; category: Category };

interface DropdownPosition {
  left: number;
  width: number;
  openUpward: boolean;
  top?: number;
  bottom?: number;
}

export default function TreeCategoryAutocomplete({
  categories,
  categoryId,
  subcategoryId,
  onChange,
  onCategoriesChange,
  onConfirm,
  onBlur,
  placeholder,
  disabled = false,
  allowCreate = true,
  categoryType,
  forCouple = false,
  className = '',
  dataTutorial,
  internalTransferCategoryId,
  internalTransferSubcategories,
  disabledSubcategoryIds = {},
}: TreeCategoryAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isCreating, setIsCreating] = useState<'category' | 'subcategory' | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ left: 0, width: 0, openUpward: false, top: 0 });
  const [mode, setMode] = useState<DropdownMode>({ kind: 'browse' });
  const { t } = useTranslation('common');
  const { t: tSub } = useTranslation('subscription');
  const navigate = useNavigate();
  const { translateCategoryName } = useCategoryTranslation();
  const canCreateCategory = useSubscriptionStore((s) => s.canCreate('customCategories'));

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const DROPDOWN_MAX_HEIGHT = 280;

  const getName = useCallback((id: string, name: string, isDefault?: boolean) => {
    return translateCategoryName(id, name, isDefault);
  }, [translateCategoryName]);

  const isTransferCategory = useCallback((cat: Category) => {
    return internalTransferCategoryId && cat.id === internalTransferCategoryId;
  }, [internalTransferCategoryId]);

  const getSubsForCategory = useCallback((cat: Category): (Subcategory & { parentCategoryId: string })[] => {
    if (isTransferCategory(cat) && internalTransferSubcategories) {
      return internalTransferSubcategories.map(s => ({ ...s, parentCategoryId: cat.id }));
    }
    return cat.subcategories.map(s => ({ ...s, parentCategoryId: cat.id }));
  }, [isTransferCategory, internalTransferSubcategories]);

  // Toutes les categories non-transfert
  const nonTransferCategories = useMemo(() =>
    categories.filter(c => !isTransferCategory(c)),
    [categories, isTransferCategory]
  );

  // Position du dropdown
  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
      const dropdownWidth = Math.max(rect.width, 240);
      const margin = 8;
      const left = Math.max(margin, Math.min(rect.left, viewportWidth - dropdownWidth - margin));
      if (openUpward) {
        setDropdownPosition({ left, width: rect.width, openUpward: true, bottom: viewportHeight - rect.top + 4, top: undefined });
      } else {
        setDropdownPosition({ left, width: rect.width, openUpward: false, top: rect.bottom + 4, bottom: undefined });
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) updateDropdownPosition();
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = () => updateDropdownPosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [isOpen, updateDropdownPosition]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    setMode({ kind: 'browse' });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        closeDropdown();
        onBlur?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onBlur, closeDropdown]);

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const item = dropdownRef.current.querySelector(`[data-selectable-index="${highlightedIndex}"]`) as HTMLElement;
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const focusNextField = useCallback(() => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        const focusableSelector = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([tabindex="-1"])';
        const allFocusable = Array.from(document.querySelectorAll(focusableSelector)) as HTMLElement[];
        const currentIndex = allFocusable.indexOf(inputRef.current!);
        if (currentIndex >= 0 && currentIndex < allFocusable.length - 1) {
          allFocusable[currentIndex + 1].focus();
        }
      }, 0);
    });
  }, []);

  // Affichage de la selection actuelle
  const selectedDisplay = useMemo(() => {
    if (!categoryId || !subcategoryId) return '';
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return '';
    const subs = getSubsForCategory(cat);
    const sub = subs.find(s => s.id === subcategoryId);
    if (!sub) return getName(cat.id, cat.name, cat.isDefault);
    return `${getName(cat.id, cat.name, cat.isDefault)} › ${getName(sub.id, sub.name, sub.isDefault)}`;
  }, [categoryId, subcategoryId, categories, getName, getSubsForCategory]);

  // === Actions ===

  const handleSelectSubcategory = (parentCategoryId: string, sub: Subcategory, moveToNext = false) => {
    onChange(parentCategoryId, sub.id);
    closeDropdown();
    onBlur?.();
    if (moveToNext) {
      onConfirm?.();
      if (onConfirm) {
        focusNextField();
      }
    }
  };

  const fetchPlanInfo = useSubscriptionStore((s) => s.fetchPlanInfo);

  const handleCreateSubcategory = async (parentCategory: Category, name: string, moveToNext = false): Promise<void> => {
    if (!name || !categoryType || isCreating) return;

    setIsCreating('subcategory');
    try {
      const response = await api.post(`/categories/${categoryType}/${parentCategory.id}/subcategories`, {
        name,
        forCouple,
      });
      if (response.data.success) {
        const newSubcategory = response.data.data;
        if (onCategoriesChange) {
          const updatedCategories = categories.map(cat =>
            cat.id === parentCategory.id
              ? { ...cat, subcategories: [...cat.subcategories, newSubcategory] }
              : cat
          );
          onCategoriesChange(updatedCategories);
        }
        onChange(parentCategory.id, newSubcategory.id);
        closeDropdown();
        fetchPlanInfo();
        if (moveToNext) {
          onConfirm?.();
          if (onConfirm) {
            focusNextField();
          }
        }
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { code?: string } } };
      if (axiosError.response?.data?.code === 'PLAN_LIMIT_REACHED') {
        fetchPlanInfo();
        closeDropdown();
      }
      console.error('Erreur lors de la creation de la sous-categorie:', error);
    } finally {
      setIsCreating(null);
    }
  };

  const handleCreateCategory = async (): Promise<void> => {
    const newName = search.trim();
    if (!newName || !categoryType || isCreating) return;

    setIsCreating('category');
    try {
      const catResponse = await api.post(`/categories/${categoryType}`, {
        name: newName,
        forCouple,
      });
      if (catResponse.data.success) {
        const newCategory: Category = { ...catResponse.data.data, subcategories: [] };
        if (onCategoriesChange) {
          onCategoriesChange([...categories, newCategory]);
        }
        // Passer en mode "nameSubcategory"
        setMode({ kind: 'nameSubcategory', category: newCategory });
        setSearch('');
        setHighlightedIndex(0);
        inputRef.current?.focus();
        fetchPlanInfo();
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { code?: string } } };
      if (axiosError.response?.data?.code === 'PLAN_LIMIT_REACHED') {
        fetchPlanInfo();
        closeDropdown();
      }
      console.error('Erreur lors de la creation de la categorie:', error);
    } finally {
      setIsCreating(null);
    }
  };

  // === Contenu du dropdown selon le mode ===

  // Mode BROWSE : liste arborescente + boutons de creation
  const browseContent = useMemo(() => {
    if (mode.kind !== 'browse') return { elements: [] as React.ReactNode[], selectableCount: 0 };

    const elements: React.ReactNode[] = [];
    let selectableIdx = 0;
    const lowerSearch = search.toLowerCase().trim();

    // Verifier l'existence globale
    let exactCategoryMatch = false;
    let exactSubcategoryMatch = false; // Le nom existe deja comme sous-categorie quelque part
    if (lowerSearch) {
      for (const cat of categories) {
        if (getName(cat.id, cat.name, cat.isDefault).toLowerCase() === lowerSearch) {
          exactCategoryMatch = true;
        }
        const subs = getSubsForCategory(cat);
        if (subs.some(s => getName(s.id, s.name, s.isDefault).toLowerCase() === lowerSearch)) {
          exactSubcategoryMatch = true;
        }
      }
    }

    // Categories et sous-categories
    for (const cat of categories) {
      const catName = getName(cat.id, cat.name, cat.isDefault).toLowerCase();
      const subs = getSubsForCategory(cat);
      const catMatches = !lowerSearch || catName.includes(lowerSearch);

      const matchingSubs = lowerSearch
        ? subs.filter(s => {
            const subName = getName(s.id, s.name, s.isDefault).toLowerCase();
            return subName.includes(lowerSearch) || catMatches;
          })
        : subs;

      if (matchingSubs.length > 0 || (catMatches && !lowerSearch)) {
        elements.push(
          <div
            key={`header-${cat.id}`}
            className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 cursor-default flex items-center gap-1.5 sticky top-0"
          >
            {cat.icon && <DynamicIcon name={cat.icon} className="w-3.5 h-3.5 flex-shrink-0" />}
            <span>{getName(cat.id, cat.name, cat.isDefault)}</span>
          </div>
        );
        for (const sub of matchingSubs) {
          const isDisabled = sub.id in disabledSubcategoryIds;
          const idx = isDisabled ? -1 : selectableIdx++;
          const isSelected = sub.parentCategoryId === categoryId && sub.id === subcategoryId;
          elements.push(
            <SubcategoryButton
              key={`sub-${sub.parentCategoryId}-${sub.id}`}
              index={idx}
              name={getName(sub.id, sub.name, sub.isDefault)}
              icon={sub.icon}
              isSelected={isSelected}
              isHighlighted={!isDisabled && idx === highlightedIndex}
              isDisabled={isDisabled}
              disabledReason={disabledSubcategoryIds[sub.id]}
              onSelect={() => handleSelectSubcategory(sub.parentCategoryId, sub, true)}
              onHover={() => { if (!isDisabled) setHighlightedIndex(idx); }}
            />
          );
        }
      }
    }

    // Boutons de creation
    const hasCreateAbility = allowCreate && categoryType && lowerSearch;
    if (hasCreateAbility) {
      if (!canCreateCategory) {
        // Limite atteinte : afficher un message d'upgrade
        elements.push(
          <button
            key="upgrade-prompt"
            type="button"
            className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-primary-600 border-t hover:bg-primary-50"
            onClick={() => navigate('/dashboard/abonnement')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Crown className="w-4 h-4" />
            <span className="flex-1 truncate">{tSub('limits.upgradeCategoriesLimit')}</span>
          </button>
        );
      } else {
        // "Ajouter sous-categorie" si ce nom n'existe pas deja comme sous-categorie
        if (!exactSubcategoryMatch) {
          const idx = selectableIdx++;
          elements.push(
            <CreateButton
              key="create-sub"
              index={idx}
              icon={<Plus className="w-4 h-4" />}
              label={<>{t('categories.addSubcategory')} : <strong>"{search}"</strong></>}
              isHighlighted={idx === highlightedIndex}
              isLoading={false}
              onSelect={() => {
                setMode({ kind: 'pickParent', subcategoryName: search.trim() });
                setSearch('');
                setHighlightedIndex(0);
              }}
              onHover={() => setHighlightedIndex(idx)}
            />
          );
        }

        // "Ajouter categorie"
        if (!exactCategoryMatch) {
          const idx = selectableIdx++;
          elements.push(
            <CreateButton
              key="create-cat"
              index={idx}
              icon={isCreating === 'category' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
              label={<>{t('categories.addCategory')} : <strong>"{search}"</strong></>}
              isHighlighted={idx === highlightedIndex}
              isLoading={isCreating === 'category'}
              onSelect={() => handleCreateCategory()}
              onHover={() => setHighlightedIndex(idx)}
            />
          );
        }
      }
    }

    if (elements.length === 0) {
      elements.push(
        <div key="no-result" className="px-3 py-2 text-sm text-gray-500">
          {t('categories.noResult')}
        </div>
      );
    }

    return { elements, selectableCount: selectableIdx };
  }, [mode, categories, search, getName, getSubsForCategory, categoryId, subcategoryId, highlightedIndex, allowCreate, categoryType, isCreating, nonTransferCategories, canCreateCategory, navigate, tSub, t, disabledSubcategoryIds]);

  // Mode PICK PARENT : choisir la categorie parente pour la sous-categorie
  const pickParentContent = useMemo(() => {
    if (mode.kind !== 'pickParent') return { elements: [] as React.ReactNode[], selectableCount: 0 };

    const elements: React.ReactNode[] = [];
    let selectableIdx = 0;
    const lowerSearch = search.toLowerCase().trim();
    const subName = mode.subcategoryName;

    // Banniere
    elements.push(
      <div key="pick-header" className="px-3 py-2 bg-primary-50 border-b border-primary-100 flex items-center gap-2">
        <button
          type="button"
          className="p-0.5 rounded hover:bg-primary-100 text-primary-600"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setMode({ kind: 'browse' });
            setSearch(subName);
            setHighlightedIndex(0);
          }}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <span className="text-sm text-primary-700">
          {t('categories.pickParentFor', { name: subName })}
        </span>
      </div>
    );

    // Liste des categories eligibles (filtrees par recherche, exclure celles qui ont deja ce nom)
    for (const cat of nonTransferCategories) {
      const catName = getName(cat.id, cat.name, cat.isDefault);
      const catNameLower = catName.toLowerCase();

      // Filtrer par recherche
      if (lowerSearch && !catNameLower.includes(lowerSearch)) continue;

      // Exclure si cette categorie a deja une sous-categorie avec ce nom exact
      const hasSameName = cat.subcategories.some(s =>
        getName(s.id, s.name, s.isDefault).toLowerCase() === subName.toLowerCase()
      );
      if (hasSameName) continue;

      const idx = selectableIdx++;
      elements.push(
        <button
          key={`pick-${cat.id}`}
          type="button"
          data-selectable-index={idx}
          className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 ${
            idx === highlightedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
          } text-gray-700`}
          onClick={() => handleCreateSubcategory(cat, subName, true)}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => setHighlightedIndex(idx)}
        >
          {cat.icon && <DynamicIcon name={cat.icon} className="w-4 h-4 flex-shrink-0 text-gray-500" />}
          <span className="flex-1 truncate">{catName}</span>
          {isCreating === 'subcategory' && idx === highlightedIndex && (
            <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
          )}
        </button>
      );
    }

    if (selectableIdx === 0) {
      elements.push(
        <div key="no-parent" className="px-3 py-2 text-sm text-gray-500">
          {t('categories.noResult')}
        </div>
      );
    }

    return { elements, selectableCount: selectableIdx };
  }, [mode, nonTransferCategories, search, getName, highlightedIndex, isCreating, t]);

  // Mode NAME SUBCATEGORY : nommer la sous-categorie apres creation de la categorie
  const nameSubcategoryContent = useMemo(() => {
    if (mode.kind !== 'nameSubcategory') return { elements: [] as React.ReactNode[], selectableCount: 0 };

    const elements: React.ReactNode[] = [];
    let selectableIdx = 0;
    const lowerSearch = search.toLowerCase().trim();
    const cat = mode.category;
    const catName = getName(cat.id, cat.name, cat.isDefault);

    // Banniere de succes
    elements.push(
      <div key="success-banner" className="px-3 py-2.5 bg-green-50 border-b border-green-100">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium">{t('categories.categoryCreated', { name: catName })}</span>
        </div>
        <p className="text-xs text-green-600 mt-1 ml-6">
          {t('categories.nowNameSubcategory')}
        </p>
      </div>
    );

    // Si l'utilisateur a tape quelque chose, proposer de creer
    if (lowerSearch) {
      const subs = cat.subcategories || [];
      // Afficher les sous-categories existantes qui matchent
      for (const sub of subs) {
        const subNameStr = getName(sub.id, sub.name, sub.isDefault);
        if (subNameStr.toLowerCase().includes(lowerSearch)) {
          const idx = selectableIdx++;
          elements.push(
            <SubcategoryButton
              key={`sub-${cat.id}-${sub.id}`}
              index={idx}
              name={subNameStr}
              icon={sub.icon}
              isSelected={false}
              isHighlighted={idx === highlightedIndex}
              onSelect={() => handleSelectSubcategory(cat.id, sub, true)}
              onHover={() => setHighlightedIndex(idx)}
            />
          );
        }
      }

      const exactMatch = subs.some(s => getName(s.id, s.name, s.isDefault).toLowerCase() === lowerSearch);
      if (!exactMatch) {
        if (!canCreateCategory) {
          elements.push(
            <button
              key="upgrade-prompt-sub"
              type="button"
              className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-primary-600 border-t hover:bg-primary-50"
              onClick={() => navigate('/dashboard/abonnement')}
              onMouseDown={(e) => e.preventDefault()}
            >
              <Crown className="w-4 h-4" />
              <span className="flex-1 truncate">{tSub('limits.upgradeCategoriesLimit')}</span>
            </button>
          );
        } else {
          const idx = selectableIdx++;
          elements.push(
            <CreateButton
              key="create-sub-pending"
              index={idx}
              icon={isCreating === 'subcategory' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              label={<>{t('categories.addSubcategory')} : <strong>"{search}"</strong></>}
              isHighlighted={idx === highlightedIndex}
              isLoading={isCreating === 'subcategory'}
              onSelect={() => handleCreateSubcategory(cat, search.trim(), true)}
              onHover={() => setHighlightedIndex(idx)}
            />
          );
        }
      }
    } else {
      elements.push(
        <div key="hint" className="px-4 py-3 text-sm text-gray-400 italic text-center">
          {t('categories.typeSubcategoryName')}
        </div>
      );
    }

    return { elements, selectableCount: selectableIdx };
  }, [mode, search, getName, highlightedIndex, isCreating, canCreateCategory, navigate, tSub, t]);

  // Fusionner le contenu actif
  const { dropdownElements, selectableCount } = useMemo(() => {
    if (mode.kind === 'pickParent') return { dropdownElements: pickParentContent.elements, selectableCount: pickParentContent.selectableCount };
    if (mode.kind === 'nameSubcategory') return { dropdownElements: nameSubcategoryContent.elements, selectableCount: nameSubcategoryContent.selectableCount };
    return { dropdownElements: browseContent.elements, selectableCount: browseContent.selectableCount };
  }, [mode, browseContent, pickParentContent, nameSubcategoryContent]);

  // === Keyboard ===

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true);
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        setHighlightedIndex(prev => Math.min(prev + 1, selectableCount - 1));
        e.preventDefault();
        break;
      case 'ArrowUp':
        setHighlightedIndex(prev => Math.max(prev - 1, 0));
        e.preventDefault();
        break;
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        // Simuler un clic sur l'element highlighted
        if (selectableCount > 0) {
          const target = highlightedIndex < selectableCount ? highlightedIndex : 0;
          const el = dropdownRef.current?.querySelector(`[data-selectable-index="${target}"]`) as HTMLElement;
          el?.click();
        } else {
          closeDropdown();
          onConfirm?.();
          if (onConfirm) {
            focusNextField();
          }
        }
        break;
      case 'Tab':
        if (selectableCount > 0) {
          const target = highlightedIndex < selectableCount ? highlightedIndex : 0;
          const el = dropdownRef.current?.querySelector(`[data-selectable-index="${target}"]`) as HTMLElement;
          el?.click();
        } else {
          closeDropdown();
        }
        onConfirm?.();
        break;
      case 'Escape':
        if (mode.kind !== 'browse') {
          // Retour au mode browse
          setMode({ kind: 'browse' });
          setSearch('');
          setHighlightedIndex(0);
        } else {
          closeDropdown();
        }
        e.stopPropagation();
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setHighlightedIndex(0);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      updateDropdownPosition();
    }
  };

  const handleToggle = () => {
    if (!disabled) {
      if (isOpen) {
        closeDropdown();
      } else {
        setIsOpen(true);
        setHighlightedIndex(0);
        updateDropdownPosition();
      }
    }
  };

  const resolvedPlaceholder = mode.kind === 'nameSubcategory'
    ? t('categories.typeSubcategoryName')
    : mode.kind === 'pickParent'
      ? t('categories.filterCategories')
      : (placeholder || t('categories.selectOrType'));

  const dropdown = isOpen && !disabled && createPortal(
    <div
      ref={dropdownRef}
      data-category-dropdown="true"
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto"
      style={{
        left: dropdownPosition.left,
        width: Math.max(dropdownPosition.width, 240),
        maxHeight: DROPDOWN_MAX_HEIGHT,
        ...(dropdownPosition.openUpward
          ? { bottom: dropdownPosition.bottom }
          : { top: dropdownPosition.top }
        ),
      }}
    >
      {dropdownElements}
    </div>,
    document.body
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`input py-1.5 text-sm pr-8 w-full ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${mode.kind !== 'browse' ? 'ring-2 ring-primary-300' : ''}`}
          placeholder={selectedDisplay || resolvedPlaceholder}
          value={isOpen ? search : selectedDisplay}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          data-tutorial={dataTutorial}
        />
        <button
          type="button"
          onClick={handleToggle}
          className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${disabled ? 'cursor-not-allowed' : ''}`}
          tabIndex={-1}
          disabled={disabled}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {dropdown}
    </div>
  );
}

// === Sous-composants ===

function SubcategoryButton({ index, name, icon, isSelected, isHighlighted, isDisabled, disabledReason, onSelect, onHover }: {
  index: number;
  name: string;
  icon: string | null;
  isSelected: boolean;
  isHighlighted: boolean;
  isDisabled?: boolean;
  disabledReason?: string;
  onSelect: () => void;
  onHover: () => void;
}) {
  if (isDisabled) {
    return (
      <div
        className="w-full pl-7 pr-3 py-2 text-left text-sm flex items-center gap-2 text-gray-400 cursor-not-allowed"
        title={disabledReason}
      >
        {icon && <DynamicIcon name={icon} className="w-4 h-4 flex-shrink-0 text-gray-300" />}
        <span className="flex-1 truncate">{name}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-selectable-index={index}
      className={`w-full pl-7 pr-3 py-2 text-left text-sm flex items-center gap-2 ${
        isHighlighted ? 'bg-gray-100' : 'hover:bg-gray-50'
      } ${isSelected ? 'text-primary-600 font-medium' : 'text-gray-700'}`}
      onClick={onSelect}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onHover}
    >
      {icon && <DynamicIcon name={icon} className="w-4 h-4 flex-shrink-0 text-gray-500" />}
      <span className="flex-1 truncate">{name}</span>
      {isSelected && <Check className="w-4 h-4 flex-shrink-0" />}
    </button>
  );
}

function CreateButton({ index, icon, label, isHighlighted, isLoading, onSelect, onHover }: {
  index: number;
  icon: React.ReactNode;
  label: React.ReactNode;
  isHighlighted: boolean;
  isLoading: boolean;
  onSelect: () => void;
  onHover: () => void;
}) {
  return (
    <button
      type="button"
      data-selectable-index={index}
      className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-primary-600 border-t ${
        isHighlighted ? 'bg-primary-50' : 'hover:bg-primary-50'
      } ${isLoading ? 'cursor-wait opacity-70' : ''}`}
      onClick={onSelect}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onHover}
      disabled={isLoading}
    >
      {icon}
      <span className="flex-1 truncate">{label}</span>
    </button>
  );
}
